import { logos } from '../../assets/logos';

/**
 * The tools the nav can render — the single table every consumer derives from.
 *
 * Deliberately **not** keyed on the codegen `ProductName`. That enum lives in
 * `packages/api-services/generated`, which is gitignored and regenerated per
 * machine from a live endpoint, so a type anchored on it is neither reviewable
 * nor reproducible — and design-system builds through Vite, which strips types
 * without checking them, so `satisfies Record<ProductName, …>` would enforce
 * nothing here. `registry.drift.test.ts` is the enforcement instead.
 *
 * Owning the union locally also lets a tool be registered *before* its product
 * row exists: `graphqlName` may name a product the schema does not carry yet,
 * and `enabled: false` keeps it out of the nav until it does.
 */
export type ToolId =
  | 'manager'
  | 'contactCenter'
  | 'analytics'
  | 'admin'
  | 'proStudio'
  | 'maestro';

export interface ToolDescriptor {
  /** GraphQL `ProductName` value; null where the tool has no product row at all. */
  graphqlName: string | null;
  /** Subdomain token — `<hostToken>.<env>.pypestream.dev`. */
  hostToken: string;
  label: string;
  logo?: keyof typeof logos;
  /** Registered but not yet renderable: no product row, or nothing deployed. */
  enabled: boolean;
}

export const TOOLS: Record<ToolId, ToolDescriptor> = {
  manager: {
    graphqlName: 'ORGANIZATION',
    hostToken: 'manager',
    label: 'Manager',
    logo: 'manager',
    enabled: true,
  },
  contactCenter: {
    graphqlName: 'AGENT_ASSIST',
    hostToken: 'contactcenter',
    label: 'Contact Center',
    logo: 'contactCenter',
    enabled: true,
  },
  analytics: {
    graphqlName: 'ANALYTICS',
    hostToken: 'analytics',
    label: 'Analytics',
    logo: 'analytics',
    enabled: true,
  },
  // No product row and no logo asset; it is a host target only.
  admin: {
    graphqlName: null,
    hostToken: 'admin',
    label: 'Admin',
    enabled: true,
  },
  proStudio: {
    graphqlName: 'PRO_STUDIO',
    hostToken: 'prostudio',
    label: 'Pro Studio',
    logo: 'proStudio',
    enabled: true,
  },
  maestro: {
    graphqlName: 'MAESTRO_STUDIO',
    hostToken: 'maestro',
    label: 'Maestro Studio',
    logo: 'maestro',
    enabled: true,
  },
};

export const toolsByGraphqlName = new Map(
  (Object.entries(TOOLS) as [ToolId, ToolDescriptor][])
    .filter(([, tool]) => tool.graphqlName !== null)
    .map(([id, tool]) => [tool.graphqlName as string, { id, ...tool }])
);

/**
 * Tools that only appear once their LaunchDarkly flag is on, keyed by the
 * GraphQL `ProductName` value.
 */
const TOOL_FEATURE_FLAGS: Record<string, string> = {
  MAESTRO_STUDIO: 'maestro-studio',
};

/**
 * Whether a tool may be shown. Absent flags read as not-released, so a flagged
 * tool stays hidden while LaunchDarkly is still loading rather than appearing
 * and then vanishing.
 */
export function isToolReleased(
  graphqlName: string | undefined,
  featureFlags?: Record<string, boolean>
): boolean {
  const flag = graphqlName ? TOOL_FEATURE_FLAGS[graphqlName] : undefined;
  return flag === undefined || Boolean(featureFlags?.[flag]);
}
